/**
 * TotalConnectMeetingsView - Video meetings surface for the Total Connect RTC widget
 *
 * Layout mirrors DialPadView so the "Video" mode of the center tab feels like
 * the same phone UI repointed at meetings:
 *   - status line (meeting defaults instead of SIP extension)
 *   - chromeless-ish input (meeting link/ID instead of a dialled number)
 *   - content area (upcoming meetings instead of digit grid)
 *   - hero FAB row (New meeting + Schedule flank, clear-input slot)
 *   - footer hint
 */

'use client';

import React, { useMemo, useState } from 'react';
import {
  ArrowRight,
  Calendar,
  CalendarPlus,
  ClipboardPaste,
  Loader2,
  Video,
  X,
} from 'lucide-react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { format, isToday, isTomorrow } from 'date-fns';
import { isCapacitor } from '@/lib/platformUtils';
import { cn } from '@/lib/utils';
import type { MeetingSettings } from '@/components/call';
import type { CalendarEvent } from '@/app/client-zone/modules/total-connect/types';

export type MeetingJoinTarget =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string };

/**
 * Resolve pasted input — a Total Connect invite link, a guest join token, a raw
 * meeting/display ID, or an external conferencing URL — into a join target.
 * Mirrors EmbeddedCalls.parseMeetingInput, plus passthrough for external links.
 */
export function resolveMeetingJoinTarget(input: string): MeetingJoinTarget | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (trimmed.includes('/meeting/join')) {
    try {
      const candidate = trimmed.startsWith('http')
        ? trimmed
        : `https://dummy.local${trimmed.startsWith('/') ? '' : '/'}${trimmed}`;
      const url = new URL(candidate);
      const token = url.searchParams.get('token');
      if (token) return { kind: 'internal', path: `/meeting/join?token=${encodeURIComponent(token)}` };
      const mid = url.searchParams.get('meeting_id');
      if (mid) return { kind: 'internal', path: `/meeting/${encodeURIComponent(mid)}` };
    } catch {
      const tokenMatch = trimmed.match(/[?&]token=([^&]+)/);
      if (tokenMatch?.[1]) {
        return { kind: 'internal', path: `/meeting/join?token=${encodeURIComponent(decodeURIComponent(tokenMatch[1]))}` };
      }
      const idMatch = trimmed.match(/[?&]meeting_id=([^&]+)/);
      if (idMatch?.[1]) {
        return { kind: 'internal', path: `/meeting/${encodeURIComponent(decodeURIComponent(idMatch[1]))}` };
      }
    }
  }

  if (trimmed.includes('/meeting/')) {
    const match = trimmed.match(/\/meeting\/([^/?]+)/);
    if (match?.[1]) return { kind: 'internal', path: `/meeting/${encodeURIComponent(match[1])}` };
  }

  if (trimmed.includes('/meet/')) {
    const id = trimmed.split('/meet/').pop();
    if (id) return { kind: 'internal', path: `/meeting/${encodeURIComponent(id)}` };
  }

  // Full external link (e.g. a third-party conferencing URL on a calendar event)
  if (/^https?:\/\//i.test(trimmed)) {
    return { kind: 'external', url: trimmed };
  }

  // Bare meeting ID / display ID (e.g. abc-defg-hij)
  return { kind: 'internal', path: `/meeting/${encodeURIComponent(trimmed)}` };
}

export interface TotalConnectMeetingsViewProps {
  isDark: boolean;
  primaryColor: string;
  /** Meeting defaults (lobby, auto-admit, join muted) shown in the status line. */
  meetingSettings?: MeetingSettings;
  /** True while the instant-meeting room is being created (FAB shows spinner). */
  startingMeeting: boolean;
  onStartMeeting: () => void;
  onScheduleMeeting: () => void;
  /** Called with a resolved join target — parent navigates and closes the widget. */
  onJoinMeeting: (target: MeetingJoinTarget) => void;
  /** Upcoming calendar events flagged as video meetings (next few, sorted). */
  upcomingMeetings?: CalendarEvent[];
  /** Opens a scheduled event's meetingUrl (internal path or external link). */
  onOpenMeetingLink?: (url: string) => void;
}

function meetingTimeLabel(start: Date): string {
  if (isToday(start)) return `Today at ${format(start, 'h:mm a')}`;
  if (isTomorrow(start)) return `Tomorrow at ${format(start, 'h:mm a')}`;
  return format(start, 'EEE d MMM, h:mm a');
}

export function TotalConnectMeetingsView({
  isDark,
  primaryColor,
  meetingSettings,
  startingMeeting,
  onStartMeeting,
  onScheduleMeeting,
  onJoinMeeting,
  upcomingMeetings = [],
  onOpenMeetingLink,
}: TotalConnectMeetingsViewProps) {
  const [joinValue, setJoinValue] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);

  const doHaptic = async (style: ImpactStyle = ImpactStyle.Light) => {
    if (isCapacitor()) {
      try {
        await Haptics.impact({ style });
      } catch {
        // Ignore haptic errors
      }
    }
  };

  const statusHint = useMemo(() => {
    if (!meetingSettings) return null;
    const parts: string[] = [
      meetingSettings.enableLobby
        ? 'Waiting room on'
        : meetingSettings.autoAdmit
          ? 'Guests auto-admitted'
          : 'Host admits guests',
    ];
    if (meetingSettings.muteOnEntry) parts.push('join muted');
    if (meetingSettings.disableVideo) parts.push('camera off');
    return parts.join(' • ');
  }, [meetingSettings]);

  const submitJoin = () => {
    if (!joinValue.trim()) return;
    const target = resolveMeetingJoinTarget(joinValue);
    if (!target) {
      setJoinError('Paste a meeting link or enter a meeting ID.');
      return;
    }
    setJoinError(null);
    doHaptic(ImpactStyle.Medium);
    onJoinMeeting(target);
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setJoinValue(text.trim());
        setJoinError(null);
        doHaptic(ImpactStyle.Light);
      }
    } catch {
      // Clipboard read can be denied — leave the field for manual paste
    }
  };

  const muted = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputShell = cn(
    'flex w-full max-w-md items-center gap-1 rounded-full border py-1.5 pl-4 pr-1.5 transition-colors',
    isDark
      ? 'border-gray-700 bg-gray-800/80 focus-within:border-gray-500'
      : 'border-gray-300 bg-white focus-within:border-gray-400',
  );
  const roundSlotButton = cn(
    'flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-all duration-150 active:scale-95 sm:h-16 sm:w-16',
    isDark
      ? 'bg-gray-800 text-gray-300 ring-1 ring-gray-700 hover:bg-gray-700'
      : 'bg-white text-gray-600 shadow-lg ring-1 ring-gray-200 hover:bg-gray-50',
  );

  return (
    <div className="flex min-h-full flex-col">
      {/* Status line + join input (analog of extension status + dialled number) */}
      <div className="px-3 pb-2 pt-6 sm:px-8 sm:pb-6 sm:pt-8">
        <div className={cn('mb-2 text-center text-xs sm:mb-3 sm:text-sm', muted)}>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
            Video meetings ready{statusHint ? ` • ${statusHint}` : ''}
          </span>
        </div>

        <div className="relative flex w-full flex-col items-center gap-2 px-1">
          <div className={inputShell}>
            <input
              type="text"
              value={joinValue}
              onChange={(e) => {
                setJoinValue(e.target.value);
                if (joinError) setJoinError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitJoin();
                }
              }}
              placeholder="Paste a meeting link or ID"
              aria-label="Meeting link or ID"
              className={cn(
                'min-w-0 flex-1 bg-transparent text-sm outline-none',
                isDark
                  ? 'text-white placeholder-gray-500'
                  : 'text-gray-900 placeholder-gray-400',
              )}
            />
            {typeof navigator !== 'undefined' && !!navigator.clipboard?.readText && (
              <button
                type="button"
                onClick={() => void pasteFromClipboard()}
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors',
                  isDark
                    ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                    : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
                )}
                title="Paste from clipboard"
                aria-label="Paste from clipboard"
              >
                <ClipboardPaste className="h-4 w-4" />
              </button>
            )}
            {joinValue.trim() && (
              <button
                type="button"
                onClick={submitJoin}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
                title="Join meeting"
                aria-label="Join meeting"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
          {joinError ? (
            <p className="text-xs text-red-500">{joinError}</p>
          ) : (
            <p className={cn('text-center text-xs', isDark ? 'text-gray-500' : 'text-gray-400')}>
              Paste a Total Connect invite link, guest link, or meeting ID to join.
            </p>
          )}
        </div>
      </div>

      {/* Upcoming meetings (occupies the digit-grid slot) */}
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col px-6 py-1 sm:px-8 sm:py-4">
        <p
          className={cn(
            'mb-2 text-[10px] font-semibold uppercase tracking-wide',
            isDark ? 'text-gray-500' : 'text-gray-400',
          )}
        >
          Upcoming meetings
        </p>
        {upcomingMeetings.length === 0 ? (
          <div
            className={cn(
              'rounded-xl border border-dashed px-4 py-6 text-center',
              isDark ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400',
            )}
          >
            <Calendar className="mx-auto mb-2 h-7 w-7 opacity-60" />
            <p className="text-sm font-medium">No upcoming meetings</p>
            <p className="mt-0.5 text-xs">Start one now or schedule for later.</p>
          </div>
        ) : (
          <ul
            className={cn(
              'divide-y overflow-hidden rounded-xl border',
              isDark
                ? 'divide-gray-800 border-gray-800 bg-gray-900/40'
                : 'divide-gray-100 border-gray-200 bg-gray-50/60',
            )}
          >
            {upcomingMeetings.map((event) => {
              const start = event.start instanceof Date ? event.start : new Date(event.start);
              const meetingUrl = event.meetingUrl;
              return (
                <li key={event.id} className="flex items-center gap-3 px-3 py-2.5">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${primaryColor}1a`, color: primaryColor }}
                  >
                    <Video className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'truncate text-sm font-medium',
                        isDark ? 'text-gray-100' : 'text-gray-800',
                      )}
                    >
                      {event.title || 'Meeting'}
                    </p>
                    <p className={cn('text-xs', isDark ? 'text-gray-500' : 'text-gray-400')}>
                      {meetingTimeLabel(start)}
                      {event.isTeamEvent && event.teamName ? ` • ${event.teamName}` : ''}
                    </p>
                  </div>
                  {meetingUrl && onOpenMeetingLink && (
                    <button
                      type="button"
                      onClick={() => {
                        doHaptic(ImpactStyle.Light);
                        onOpenMeetingLink(meetingUrl);
                      }}
                      className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                      style={{ backgroundColor: primaryColor }}
                    >
                      Join
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Hero action row (analog of dial FAB row): Schedule | New meeting | clear */}
      <div className="px-6 pb-4 pt-2 sm:pb-6">
        <div className="relative flex items-center justify-center gap-3 sm:gap-6">
          <div className="flex w-12 shrink-0 flex-col items-center gap-1 sm:w-16">
            <button
              type="button"
              onClick={() => {
                doHaptic(ImpactStyle.Light);
                onScheduleMeeting();
              }}
              className={roundSlotButton}
              title="Schedule meeting"
              aria-label="Schedule meeting"
            >
              <CalendarPlus className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            <span className={cn('text-[10px] font-medium', muted)}>Schedule</span>
          </div>

          <div className="flex shrink-0 flex-col items-center gap-1">
            <button
              type="button"
              onClick={() => {
                doHaptic(ImpactStyle.Heavy);
                onStartMeeting();
              }}
              disabled={startingMeeting}
              className={cn(
                'flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-white shadow-lg transition-all duration-200 active:scale-95 sm:h-20 sm:w-20',
                startingMeeting
                  ? 'cursor-wait opacity-80'
                  : 'hover:opacity-90 hover:shadow-xl',
              )}
              style={{
                backgroundColor: primaryColor,
                boxShadow: `0 8px 25px -5px ${primaryColor}66`,
              }}
              title="Start meeting now"
              aria-label="Start meeting now"
            >
              {startingMeeting ? (
                <Loader2 className="h-7 w-7 animate-spin sm:h-9 sm:w-9" />
              ) : (
                <Video className="h-7 w-7 sm:h-9 sm:w-9" />
              )}
            </button>
            <span className={cn('text-[10px] font-medium', muted)}>
              {startingMeeting ? 'Starting…' : 'New meeting'}
            </span>
          </div>

          <div className="flex w-12 shrink-0 flex-col items-center gap-1 sm:w-16">
            {joinValue ? (
              <button
                type="button"
                onClick={() => {
                  setJoinValue('');
                  setJoinError(null);
                  doHaptic(ImpactStyle.Light);
                }}
                className={roundSlotButton}
                title="Clear"
                aria-label="Clear meeting link"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Footer hint (desktop only, same as DialPadView) */}
      <div
        className={cn(
          'hidden border-t px-4 py-3 text-center sm:block sm:px-6 sm:py-4',
          isDark ? 'border-gray-800 bg-gray-900/50' : 'border-gray-100 bg-gray-50/50',
        )}
      >
        <p className={cn('text-xs', isDark ? 'text-gray-500' : 'text-gray-400')}>
          Meetings open in a secure video room — invite guests, share your screen, or use the whiteboard from inside.
        </p>
        <p className={cn('mt-0.5 text-xs', isDark ? 'text-gray-600' : 'text-gray-300')}>
          Scheduled meetings are saved to your Total Connect calendar.
        </p>
      </div>
    </div>
  );
}

export default TotalConnectMeetingsView;
